import * as style from './style.module.scss'
import GearIcon from '../icons/GearIcon'
import classNames from 'classnames'
import Link from 'gatsby-link'
import * as React from 'react'
import { useCrmListingAccount } from '../../hooks/useCrmListingAccount'
import { logout as logoutSession } from '../../utils/authSession'

function ChevronDownIcon({ className }) {
  return (
    <svg
      className={classNames(style.chevron, className)}
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <polyline points='6 9 12 15 18 9' />
    </svg>
  )
}

function DashboardIcon({ className }) {
  return (
    <svg
      className={classNames(style.navIcon, className)}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <rect x='3' y='4' width='18' height='18' rx='2' />
      <line x1='16' y1='2' x2='16' y2='6' />
      <line x1='8' y1='2' x2='8' y2='6' />
      <line x1='3' y1='10' x2='21' y2='10' />
    </svg>
  )
}

function MessagesIcon({ className }) {
  return (
    <svg
      className={classNames(style.navIcon, className)}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
    </svg>
  )
}

function WorkspaceSettingsIcon({ className }) {
  return (
    <GearIcon className={classNames(style.navIcon, className)} aria-hidden />
  )
}

function TeamIcon({ className }) {
  return (
    <svg
      className={classNames(style.navIcon, className)}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' />
      <circle cx='9' cy='7' r='4' />
      <path d='M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75' />
    </svg>
  )
}

function PaymentsIcon({ className }) {
  return (
    <svg
      className={classNames(style.navIcon, className)}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M12 3l9 6H3l9-6z' />
      <path d='M6 9v11' />
      <path d='M12 9v11' />
      <path d='M18 9v11' />
      <path d='M3 20h18' />
    </svg>
  )
}

/**
 * @param {'default' | 'dashboard'} [props.layout]
 * @param {string} [props.accountLogoSrc] — square logo image URL
 * @param {number} [props.messageBadgeCount]
 * @param {'dashboard' | 'messages'} [props.activeItem]
 * @param {boolean} [props.accountSettingsNav] — dashboard layout; show Workspace / Team / Payments instead of Dashboard / Messages; account menu shows “Dashboard” instead of “Settings”
 * @param {'workspace' | 'team' | 'payments'} [props.accountSettingsActiveItem]
 * @param {() => void} [props.onDashboardNavClick] — dashboard layout only; runs when Dashboard link is clicked
 * @param {React.ReactNode} [props.channelListSlot] — dashboard layout only; e.g. Stream Chat list under Messages
 */
function SidebarNavigation({
  layout = 'default',
  accountLogoSrc,
  messageBadgeCount,
  activeItem,
  accountSettingsNav = false,
  accountSettingsActiveItem = 'workspace',
  onDashboardNavClick,
  channelListSlot,
}) {
  const isDashboardLayout = layout === 'dashboard'
  const crmListingAccount = useCrmListingAccount()
  /** CRM dashboard shows the linked listing business name, not the signed-in user email. */
  const dashboardAccountName = crmListingAccount.name
  const dashboardAccountLogo = accountLogoSrc ?? crmListingAccount.logoSrc
  const [accountMenuOpen, setAccountMenuOpen] = React.useState(false)
  const [chatListScrolled, setChatListScrolled] = React.useState(false)
  const [chatListOverflowsBottom, setChatListOverflowsBottom] =
    React.useState(false)
  const accountMenuRef = React.useRef(null)
  const channelListRef = React.useRef(null)

  React.useEffect(() => {
    if (!channelListSlot) {
      setChatListScrolled(false)
      setChatListOverflowsBottom(false)
      return undefined
    }

    const root = channelListRef.current
    if (!root) {
      return undefined
    }

    const scrollSelector =
      '.str-chat__channel-list-messenger__main, .str-chat__channel-list-messenger-react__main'

    let scrollEl = null
    let mountObserver = null
    let resizeObserver = null

    const syncScrollFades = () => {
      if (!scrollEl) return
      const { scrollTop, clientHeight, scrollHeight } = scrollEl
      setChatListScrolled(scrollTop > 1)
      setChatListOverflowsBottom(scrollTop + clientHeight < scrollHeight - 1)
    }

    const bindScrollEl = (el) => {
      if (scrollEl === el) return
      if (scrollEl) {
        scrollEl.removeEventListener('scroll', syncScrollFades)
        resizeObserver?.disconnect()
      }
      scrollEl = el
      scrollEl.addEventListener('scroll', syncScrollFades, { passive: true })
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(syncScrollFades)
        resizeObserver.observe(scrollEl)
      }
      syncScrollFades()
    }

    const tryBindScrollEl = () => {
      const el = root.querySelector(scrollSelector)
      if (el) {
        bindScrollEl(el)
      }
    }

    tryBindScrollEl()

    if (!scrollEl) {
      mountObserver = new MutationObserver(tryBindScrollEl)
      mountObserver.observe(root, { childList: true, subtree: true })
    }

    return () => {
      mountObserver?.disconnect()
      resizeObserver?.disconnect()
      if (scrollEl) {
        scrollEl.removeEventListener('scroll', syncScrollFades)
      }
      setChatListScrolled(false)
      setChatListOverflowsBottom(false)
    }
  }, [channelListSlot])

  React.useEffect(() => {
    if (!accountMenuOpen) return
    const onDocMouseDown = (e) => {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(e.target)
      ) {
        setAccountMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [accountMenuOpen])

  if (isDashboardLayout) {
    return (
      <aside
        className={style.sidebarDashboard}
        aria-label='Dashboard navigation'
      >
        <div className={style.accountMenuWrap} ref={accountMenuRef}>
          <button
            type='button'
            className={style.accountSwitcher}
            aria-expanded={accountMenuOpen}
            aria-haspopup='menu'
            aria-controls='dashboard-account-menu'
            id='dashboard-account-switcher'
            onClick={() => setAccountMenuOpen((open) => !open)}
          >
            <span className={style.accountSwitcherMain}>
              <img
                src={dashboardAccountLogo}
                alt=''
                className={style.accountSwitcherLogo}
                width={28}
                height={28}
              />
              <span className={style.accountSwitcherLabel}>
                {dashboardAccountName}
              </span>
            </span>
            <ChevronDownIcon
              className={accountMenuOpen ? style.chevronOpen : undefined}
            />
          </button>
          {accountMenuOpen ? (
            <div
              id='dashboard-account-menu'
              className={style.accountMenuDropdown}
              role='menu'
              aria-labelledby='dashboard-account-switcher'
            >
              {accountSettingsNav ? (
                <Link
                  to='/dashboard/'
                  className={classNames(
                    style.accountMenuItem,
                    style.accountMenuItemDashboard,
                  )}
                  role='menuitem'
                  onClick={() => {
                    setAccountMenuOpen(false)
                    onDashboardNavClick?.()
                  }}
                >
                  <DashboardIcon className={style.navIconDashboard} />
                  <span className={style.accountMenuItemDashboardLabel}>
                    Dashboard
                  </span>
                </Link>
              ) : (
                <Link
                  to='/account/'
                  className={classNames(
                    style.accountMenuItem,
                    style.accountMenuItemWithNavIcon,
                    style.accountMenuItemBlueRing,
                  )}
                  role='menuitem'
                  onClick={() => setAccountMenuOpen(false)}
                >
                  <WorkspaceSettingsIcon
                    className={style.navIconDashboard}
                  />
                  <span className={style.accountMenuItemDashboardLabel}>
                    Settings
                  </span>
                </Link>
              )}
              <button
                type='button'
                className={classNames(
                  style.accountMenuItem,
                  style.accountMenuItemBlueRing,
                )}
                role='menuitem'
                onClick={() => {
                  setAccountMenuOpen(false)
                  logoutSession()
                }}
              >
                Log out
              </button>
            </div>
          ) : null}
        </div>
        <nav
          className={classNames(style.pageListDashboard, {
            [style.pageListDashboardChatFade]:
              Boolean(channelListSlot) && chatListScrolled,
          })}
        >
          {accountSettingsNav ? (
            <>
              <Link
                className={classNames(style.pageLinkDashboard, {
                  [style.pageLinkDashboardActive]:
                    accountSettingsActiveItem === 'workspace',
                })}
                to='/account/'
              >
                <WorkspaceSettingsIcon className={style.navIconDashboard} />
                <span>Workspace</span>
              </Link>
              <Link
                className={classNames(style.pageLinkDashboard, {
                  [style.pageLinkDashboardActive]:
                    accountSettingsActiveItem === 'team',
                })}
                to='/account/team/'
              >
                <TeamIcon />
                <span>Team</span>
              </Link>
              <Link
                className={classNames(style.pageLinkDashboard, {
                  [style.pageLinkDashboardActive]:
                    accountSettingsActiveItem === 'payments',
                })}
                to='/account/payments/'
              >
                <PaymentsIcon />
                <span>Payments</span>
              </Link>
            </>
          ) : (
            <>
              <Link
                className={classNames(style.pageLinkDashboard, {
                  [style.pageLinkDashboardActive]: activeItem === 'dashboard',
                })}
                to='/dashboard/'
                onClick={() => onDashboardNavClick?.()}
              >
                <DashboardIcon className={style.navIconDashboard} />
                <span>Dashboard</span>
              </Link>
              <Link
                className={classNames(style.pageLinkDashboard, {
                  [style.pageLinkDashboardActive]: activeItem === 'messages',
                })}
                to='/messages/'
              >
                <MessagesIcon />
                <span className={style.messagesLabel}>Messages</span>
                {messageBadgeCount != null && messageBadgeCount > 0 ? (
                  <span className={style.messageBadge}>{messageBadgeCount}</span>
                ) : null}
              </Link>
            </>
          )}
        </nav>
        {channelListSlot ? (
          <div
            ref={channelListRef}
            className={classNames(style.sidebarChannelList, {
              [style.sidebarChannelListBottomFade]: chatListOverflowsBottom,
              [style.sidebarChannelListHidden]: accountSettingsNav,
            })}
            id='mobile-channel-list'
          >
            {channelListSlot}
          </div>
        ) : null}
      </aside>
    )
  }

  return (
    <div className={style.sidebar}>
      <div className={style.pageList}>
        <Link className={style.pageLink} to={'/dashboard/'}>
          <strong>Dashboard</strong>
        </Link>
        <Link className={style.pageLink} to='/messages/'>
          <strong>Messages</strong>
          <span className={style.messageCount}> (1)</span>
          <span className={style.newMessage}></span>
        </Link>
      </div>
    </div>
  )
}

export { DashboardIcon, MessagesIcon, WorkspaceSettingsIcon }
export default SidebarNavigation
